import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown, Loader2, Banknote, AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { Separator } from "@/shared/ui/separator";
import { Switch } from "@/shared/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import { cn } from "@/shared/lib/utils";
import { Alert, AlertDescription } from "@/shared/ui/alert";

import type { PaymentCreatorBankAccount } from "../utils/types";
import {
  useCreateCreatorBankAccount,
  useCreatorBankAccounts,
} from "../hooks";
import { useBrasilApiBanks } from "../hooks";

// Função para normalizar texto removendo acentos
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

// Função de filtro customizada para busca de bancos
// O cmdk espera: retornar 1 se corresponde, 0 se não corresponde
const bankFilter = (value: string, search: string): number => {
  if (!search || search.trim() === "") return 1; // Se não há busca, mostrar todos
  const normalizedValue = normalizeText(value);
  const normalizedSearch = normalizeText(search.trim());
  
  // Se o valor contém a busca, retornar 1 (mostrar)
  if (normalizedValue.includes(normalizedSearch)) {
    return 1;
  }
  
  // Se não corresponde, retornar 0 (não mostrar)
  return 0;
};

const bankAccountSchema = z.object({
  bank: z.string().min(2, "Selecione o banco"),
  code: z.string().min(1, "Selecione o banco"),
  branch: z.string().min(1, "Informe a agência"),
  account: z.string().min(1, "Informe a conta"),
  pix: z.string().min(3, "Informe a chave PIX"),
  active: z.boolean().default(true),
  isDefault: z.boolean().default(false),
});

type BankAccountFormValues = z.infer<typeof bankAccountSchema>;

interface CreatorBankAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creatorId?: string;
  onAccountSelected?: (account: PaymentCreatorBankAccount) => void;
}

export function CreatorBankAccountDialog({
  open,
  onOpenChange,
  creatorId,
  onAccountSelected,
}: CreatorBankAccountDialogProps) {
  const {
    bankAccounts,
    isLoading: loadingAccounts,
    refetch: refetchAccounts,
  } = useCreatorBankAccounts(creatorId, {
    enabled: open && Boolean(creatorId),
    queryKey: ["payments", "bank-accounts", creatorId],
  });

  const {
    createCreatorBankAccount,
    isLoading: creatingAccount,
  } = useCreateCreatorBankAccount();

  const { data: brasilApiBanks, isLoading: loadingBanks, error: banksError } = useBrasilApiBanks();
  const [bankSearchOpen, setBankSearchOpen] = useState(false);

  const form = useForm<BankAccountFormValues>({
    resolver: zodResolver(bankAccountSchema),
    defaultValues: {
      bank: "",
      code: "",
      branch: "",
      account: "",
      pix: "",
      active: true,
      isDefault: false,
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset({
        bank: "",
        code: "",
        branch: "",
        account: "",
        pix: "",
        active: true,
        isDefault: false,
      });
    }
  }, [open, form]);

  const handleSelectAccount = (account: PaymentCreatorBankAccount) => {
    onAccountSelected?.(account);
    onOpenChange(false);
  };

  const handleSubmit = async (values: BankAccountFormValues) => {
    if (!creatorId) return;
    const account = await createCreatorBankAccount({
      creatorId,
      bank: values.bank,
      code: values.code,
      branch: values.branch,
      account: values.account,
      pix: values.pix,
      active: values.active,
      default: values.isDefault,
    });
    await refetchAccounts();
    onAccountSelected?.(account);
    onOpenChange(false);
    form.reset({
      bank: "",
      code: "",
      branch: "",
      account: "",
      pix: "",
      active: true,
      isDefault: false,
    });
  };

  const disabled = !creatorId || creatingAccount;

  const bankOptions = useMemo(() => {
    if (!brasilApiBanks) return [];
    return brasilApiBanks.map((bank) => ({
        value: bank.code!.toString(),
        label: bank.name,
        fullName: bank.fullName,
        code: bank.code!,
      ispb: bank.ispb,
      }));
  }, [brasilApiBanks]);

  const handleBankSelect = (bankCode: string) => {
    const selectedBank = brasilApiBanks?.find((bank) => bank.code != null && bank.code.toString() === bankCode);
    if (selectedBank && selectedBank.code != null) {
      form.setValue("bank", selectedBank.name);
      form.setValue("code", selectedBank.code.toString());
    }
  };

  const selectedBankCode = form.watch("code");
  const selectedBank = useMemo(() => {
    if (!selectedBankCode || !brasilApiBanks) return null;
    return brasilApiBanks.find((b) => b.code != null && b.code.toString() === selectedBankCode) ?? null;
  }, [selectedBankCode, brasilApiBanks]);

  const selectedBankCodeFormatted = useMemo(() => {
    if (!selectedBank?.code) return "";
    return selectedBank.code.toString().padStart(3, "0");
  }, [selectedBank]);

  const selectedBankName = useMemo(() => {
    if (!selectedBank) return "";
    const name = selectedBank.fullName || selectedBank.name;
    if (name === selectedBank.code?.toString()) return selectedBank.name;
    return name;
  }, [selectedBank]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="space-y-1">
          <DialogTitle>Contas bancárias do creator</DialogTitle>
          <DialogDescription>
            Selecione uma conta existente ou cadastre uma nova para vincular ao pagamento.
          </DialogDescription>
        </DialogHeader>

        {!creatorId ? (
          <div className="rounded-md border border-dashed border-muted-foreground/40 p-6 text-center text-sm text-muted-foreground">
            Selecione um creator para gerenciar as contas bancárias disponíveis.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Contas disponíveis</h4>
                {loadingAccounts && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              {bankAccounts.length === 0 ? (
                <div className="rounded-md border border-dashed border-muted-foreground/40 p-4 text-sm text-muted-foreground">
                  Nenhuma conta cadastrada ainda.
                </div>
              ) : (
                <div className="space-y-2">
                  {bankAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="rounded-lg border border-white/10 bg-muted/10 p-4"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="space-y-1">
                          <p className="text-sm font-semibold">{account.bank}</p>
                          <p className="text-xs text-muted-foreground">
                            Agência {account.branch} • Conta {account.account}
                          </p>
                          <p className="text-xs text-muted-foreground">PIX: {account.pix}</p>
                          <div className="flex flex-wrap gap-2">
                            {account.default && (
                              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                                Preferencial
                              </Badge>
                            )}
                            {!account.active && (
                              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                Inativa
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleSelectAccount(account)}>
                          <Banknote className="mr-2 h-4 w-4" />
                          Selecionar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold">Nova conta</h4>
                <p className="text-xs text-muted-foreground">
                  Cadastre uma nova conta bancária para este creator.
                </p>
              </div>
              <Separator />
              {banksError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Erro ao carregar bancos da Brasil API. Tente novamente mais tarde.
                  </AlertDescription>
                </Alert>
              )}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
                  <FormField
                    control={form.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Banco</FormLabel>
                        <Popover modal open={bankSearchOpen} onOpenChange={setBankSearchOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                role="combobox"
                                className={cn(
                                  "w-full justify-between gap-2",
                                  !field.value && "text-muted-foreground",
                                )}
                                disabled={disabled || loadingBanks}
                              >
                                {loadingBanks ? (
                                  <span className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Carregando bancos...
                                  </span>
                                ) : selectedBank ? (
                                  <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                                    <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                                      {selectedBankCodeFormatted}
                                    </span>
                                    <span className="truncate">{selectedBankName}</span>
                                  </span>
                                ) : (
                                  "Selecione o banco"
                                )}
                                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                          </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                            <Command filter={bankFilter} shouldFilter={true}>
                              <CommandInput placeholder="Buscar banco por nome ou código..." />
                              <CommandList>
                                <CommandEmpty>
                                  {loadingBanks ? "Carregando..." : "Nenhum banco encontrado."}
                                </CommandEmpty>
                                <CommandGroup>
                                  {bankOptions.map((option) => {
                                    // Criar string de busca incluindo todas as variações possíveis
                                    const searchValue = `${option.label} ${option.code} ${option.fullName} ${option.ispb} ${normalizeText(option.label)} ${normalizeText(option.fullName)}`;
                                    return (
                                      <CommandItem
                                        key={option.value}
                                        value={searchValue}
                                        onSelect={() => {
                                          field.onChange(option.value);
                                          handleBankSelect(option.value);
                                          setBankSearchOpen(false);
                                        }}
                                      >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4 shrink-0",
                                          field.value === option.value ? "opacity-100" : "opacity-0",
                                        )}
                                      />
                                      <span className="mr-2 shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                                        {option.code.toString().padStart(3, "0")}
                                      </span>
                                      <div className="flex min-w-0 flex-1 flex-col">
                                        <span className="truncate font-medium">
                                          {option.fullName || option.label}
                                        </span>
                                        {option.label !== option.fullName &&
                                          option.label !== option.code.toString() && (
                                            <span className="truncate text-xs text-muted-foreground">
                                              {option.label}
                                            </span>
                                          )}
                                      </div>
                                    </CommandItem>
                                    );
                                  })}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        {selectedBank && (
                          <p className="text-xs text-muted-foreground">
                            ISPB: {selectedBank.ispb}
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="bank"
                    render={({ field }) => (
                      <FormItem className="hidden">
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-3 md:grid-cols-1">
                    <FormField
                      control={form.control}
                      name="branch"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Agência</FormLabel>
                          <FormControl>
                            <Input placeholder="0001" {...field} disabled={disabled} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="account"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Conta</FormLabel>
                        <FormControl>
                          <Input placeholder="12345-6" {...field} disabled={disabled} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="pix"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Chave PIX</FormLabel>
                        <FormControl>
                          <Input placeholder="email@exemplo.com" {...field} disabled={disabled} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-center justify-between rounded-md border border-white/10 p-3 text-sm">
                    <div>
                      <p className="font-medium">Marcar como preferencial</p>
                      <p className="text-xs text-muted-foreground">
                        Caso habilitado, essa conta será marcada como padrão do creator.
                      </p>
                    </div>
                    <FormField
                      control={form.control}
                      name="isDefault"
                      render={({ field }) => (
                        <FormItem className="flex items-center gap-2">
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} disabled={disabled} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <Button type="submit" disabled={disabled}>
                    {creatingAccount ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      "Cadastrar conta"
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
